import lang from 'dojo/_base/lang';
import topic from 'dojo/topic';
import _WidgetBase from 'dijit/_WidgetBase';
import Toaster from '../UI/Toaster_TS/Toaster';
import { compose, mdsWidget } from 'dojo/_base/declare';
import SessionInfo from './Session/SessionInfo';
import GenericDialog from '../UI/GenericDialog/GenericDialog';
import SignalR_Default from '../../WidgetCollection/SignalR/SignalR_Default';
import domAttr from 'dojo/dom-attr';
import ProgressBar from '../UI/ProgressBar/ProgressBar';
import ThemeToggler from '../System/ThemeToggler';
import WebPrivilegeManager from '../System/WebPrivilegeManager';
import StandbyManager from '../System/StandbyManager';
import TabManager from './TabManager';
import GenericEntityView from './../UI/EntityBased/EntityView/GenericEntityView';
import { EvBookingView, ProjectView, EvTaskView } from "../../WidgetCollection/_loadingHelpers/VAMv2";
import { VAMclassNames } from "../../WidgetCollection/_loadingHelpers/VAMv2Types";
import AddressView from '../../WidgetCollection/AddressFinder/AddressView';
import { Classnames as AddressClassnames } from "../../WidgetCollection/_loadingHelpers/Address";
import SystemStateMonitor from './SystemStateMonitor';
import Evisio from './Evisio/Evisio';
import EvisioBridgeClient from '../../WidgetCollection/evisio2/bridge/EvisioBridgeClient';
import NotificationListener from './Notifications/NotificationListener';
import { DiagnosticHelper, ExtraHelpers, FeatureHelpers } from "../../more/Helpers";
import NotificationCenter from './Notifications/NotificationCenter';
import msTeamsIntegrationManager from '../../WidgetCollection/ms_teams/TeamsIntegrationManager/msTeamsIntegrationManager';
import TranslationLoadingManager from "../Tools/TranslationLoadingManager";
import _EntityViewMixin from '../UI/EntityBased/EntityView/EntityView';
import methodHooks from 'more/mdsMethodHooks';

class GlobalWidgetsLoader extends methodHooks(compose(_WidgetBase)) {

    private toaster: MdsLax<InstanceType<typeof Toaster>> = null;
    private signalR: MdsLax<InstanceType<typeof SignalR_Default>> = null;
    private toggler: MdsLax<InstanceType<typeof ThemeToggler>> = null;
    private evisio: MdsLax<InstanceType<typeof Evisio>> = null;

    private webPrivLoader: MdsLax<InstanceType<typeof WebPrivilegeManager>> = null;
    private standbyManager: MdsLax<InstanceType<typeof StandbyManager>> = null;
    private tabManager: MdsLax<InstanceType<typeof TabManager>> = null;
    private notificationListener: MdsLax<InstanceType<typeof NotificationListener>> = null;

    private stateMonitor: MdsLax<InstanceType<typeof SystemStateMonitor>> = null;

    private msTeamsIntegrationManager: MdsLax<InstanceType<typeof msTeamsIntegrationManager>> = null;

    private init() {
        if (typeof window.localStorageShim == 'undefined'){
            window.localStorageShim = window.localStorage;
        }
        if (typeof window.isBundleRuntime != 'boolean'){
            window.isBundleRuntime = false;
        }

        // evisio2 bridge — bootstrap inside the inner iframe before any other widget so
        // the postMessage listener and Theme/Change subscription are alive before
        // ThemeToggler publishes its initial value. The `self !== top` guard skips
        // the outer shell (which also sets window.inEvisio2 but has no parent host to
        // talk to). In Teams, the workspaceRoot Teams gate prevents the host from
        // booting anyway, so a stray client would be harmless — but skipping it here
        // avoids the wasted history patches and postMessage overhead.
        if ((window as any).inEvisio2 && window.self !== window.top){
            EvisioBridgeClient.instance();
        }

        //vconsole show (nice on safari)
        try{
            if (window.location.search.toLowerCase().includes("vcon=true")) {
                console.info("Loading vconsole debug helper...");
                ExtraHelpers.sideLoadResource(window.appRoot + "Content/syncfusion/vconsoleDojo.bundle.js", 'js').then(() => {
                    console.log("Vconsole script loaded!")
                });
            }
        }catch{}
        //diagnostics - set username
        topic.subscribe("/Session/Close", lang.hitch(this, ()=>{
            DiagnosticHelper.ResetUser();
        }))
        topic.subscribe( "/Session/CheckForExistingSession", lang.hitch(this, ()=>{
            DiagnosticHelper.SetUser(window.sessionInfo.getUserName(), window.sessionInfo.getCurrentWebuserId() );
        }) );
        topic.subscribe("MsTeamsIntegration/Authenticated", ()=>{
            let username = "UNKNOWN";
            try{
                username = window.sessionInfo?.getUserName() ?? "UNKNOWN";
            }catch{}
            username = username + " (teams tab app)";
            DiagnosticHelper.SetUser(username, window.sessionInfo.getCurrentWebuserId());
        });

        //translations
        TranslationLoadingManager.getInstance().loadTranslationsFromServer();
        // Theme Toggler:
        this.toggler = new ThemeToggler();
        window.themeToggler = this.toggler;
        this.toggler.startup();

        // Evisio v1 widget always boots; its UI/behavior branches on window.inEvisio2
        // (close icon + breakout vs logo + redirect into /Evisio shell).
        // _init() in Evisio.ts already no-ops when #evisioContainer is missing (outer shell layout).
        this.evisio = new Evisio();
        this.evisio.startup();

        //ms teams integration
        this.msTeamsIntegrationManager = new msTeamsIntegrationManager();
        this.msTeamsIntegrationManager.init();

        this.stateMonitor = new SystemStateMonitor();
        this.stateMonitor.startup();

        this.tabManager = new TabManager();
        this.tabManager.startup();
        window.tabManager = this.tabManager;



        this.webPrivLoader = new WebPrivilegeManager();
        this.standbyManager = new StandbyManager();
        (window as any).standbyManager = this.standbyManager;
        (window as any).dijits = window.dijit.registry._hash;


        // SESSION INFO:
        if (!window.sessionInfo) {
            let sessionInfo = new SessionInfo();
            sessionInfo.startup();
        }


        // NOTIFICATIONS: Listener und Center separat einstellbar
        if (!window.notificationListener) {
            let listener = new NotificationListener();
            listener.startup();

            window.notificationListener = listener;
        }

        let notificatonsModule = FeatureHelpers.getModule<{ NotificationCenterTargetnode: string }>("Notifications");

        let node = document.getElementById(notificatonsModule?.NotificationCenterTargetnode ? notificatonsModule.NotificationCenterTargetnode : "notificationCenterContainer_top");

        if (node && FeatureHelpers.hasFeature("Notifications", "NotificationCenter")) {

            if (!window.notificationCenter) {
                let notificationCenter = new NotificationCenter();
                notificationCenter.placeAt(node, "only");
                notificationCenter.startup();

                window.notificationCenter = notificationCenter;
            }
        }

        if (!FeatureHelpers.hasFeature("System", "DisableSignalR")) { // SignalR opt out
            this.signalR = new SignalR_Default();
            domAttr.set(this.signalR.domNode, "data-dojo-id", "SignalR_Instance"); // ??
            (window as any).signalR = this.signalR;
        }

        // TOASTER:
        {
            let toaster: MdsLax<InstanceType<typeof Toaster>> = (window as any).toaster;

            if (!toaster) {
                toaster = new Toaster();

                (window as any).toaster = toaster;

                toaster.startup();

                toaster.addSubscription("/Toaster/ShowToast", lang.hitch(this, this._onShowToast));
                toaster.addSubscription("/Toaster/RemoveToast", lang.hitch(this, this._onRemoveToast));
            }

            this.toaster = toaster;
        }
    }

    private _onRemoveToast(toastID: string) {
        if (!this.toaster) return;

        this.toaster.removeToastById(toastID);
    }

    private _onShowToast(config: GlobalToastConfig) {

        if (!this.toaster) return;
        if (!config) return;

        if (config.message != null && typeof (config.message) != "undefined") {
            let message: string = null;
            if (typeof (config.message) == "string") {
                message = config.message;
            }
            else if ((config.message as any).toString) {
                try {
                    message = (config.message as any).toString();
                } catch (ex) {
                    console.error("_onShowToast: invalid message in config?", config, ex);
                }
            }

            if (message) {
                if (!config.options) {
                    config.options = {
                        closeButton: true,
                        timeout: "permanent"
                    };
                }

                config.options.onClick = {
                    callback: lang.hitch(this, this.showToastDialog,
                        message,
                        config.title,
                        null,
                        "close",
                        null,
                        typeof config.messageIcon != undefined ? config.messageIcon : config.iconClass,
                        config.modalSize ? config.modalSize : "smaller")
                };
            }
        }

        if (typeof (config.showClose) == "boolean") {
            if (!config.options) {
                config.options = {

                };
            }

            config.options.closeButton = config.showClose;
        }
        if (typeof (config.timeout) == "number" || config.timeout == "permanent") {
            if (!config.options) {
                config.options = {

                };
            }

            config.options.timeout = config.timeout;
        }
        if (typeof (config.ID) == "string") {
            if (!config.options) {
                config.options = {

                };
            }

            config.options.ID = config.ID;
        }

        this.toaster.addToast(config.title, config.iconClass, config.options);
    }

    private showToastDialog(message: string, header: string, subtitle: string, buttons: string, callback: Function, icon?: string, size?: "larger" | "smaller" | "fullsize") {
        let dlg = new GenericDialog();
        if (size) dlg.setSize(size);
        dlg.show(message, header, subtitle, icon, buttons as any, callback);
    }

    private subscriptions: Array<{ topic: string, handle: dojo.Handle }>;

    private progressBar: MdsLax<InstanceType<typeof ProgressBar>>;
    private _showProgressbar(type: string, data: any) {
        //console.debug(this.id + " _showProgressbar", type, data);
        if (this.progressBar) {
            this._removeProgressbar();
        }
        let bDestroy = true; // default
        if (data?.closeOnCompletion === false || data?.closeOnCompletion === true) bDestroy = data.closeOnCompletion;

        this.progressBar = new ProgressBar({
            displayType: type as any,
            heading: data.heading,
            initialText: data.text,
            textlinesCount: data.textlines,
            destroyAfterCompletion: bDestroy
        });


        //this.progressBar.on("finished", lang.hitch(this, function () { this.progressBar = null }));
    }

    private _removeProgressbar() {
        if (this.progressBar) {
            try {
                this.progressBar.destroy();
            } catch (ex) {

            }
            this.progressBar = null;
        }
    }

    private _updateProgressbar(data: any) {

        if (!this.progressBar) return;

        this.progressBar.setNewTarget(data.progress, data.text);
    }

    private _topbarUpdateHandle: any = null;
    private _onTopbarMarginRequest(delay: number = 100) {
        if (delay < 0) delay = 100;

        if (this._topbarUpdateHandle) {
            clearTimeout(this._topbarUpdateHandle);
        }

        this._topbarUpdateHandle = setTimeout(lang.hitch(this, this.updateTopbarMargin), delay);
    }

    private updateTopbarMargin() {
        let topBar = document.getElementById("topBar_layout");

        let height = window.getComputedStyle(topBar).height;

        //console.debug("updateTopbarMargin", height);

        (window as any).topBar_height = height;
        document.body.style.setProperty("--marginTopbar", height);
    }

    private _openGenericDlg(args: any/* GenericDialogTypes.DlgConfig*/) {
        let dlg = new GenericDialog();
        dlg.setup(args);
    }

    private _openEntityView(args: {
        entityId: string,
        entityClass: string,
        label: string,
        additionalParams: any,
        contextName?: string,
        tabContainerName?: string,
        targetNode?: HTMLElement,
        nonInteractive?: boolean
    }) {

        let widget: MdsLax<InstanceType<typeof _EntityViewMixin>> = null;
        switch (args?.entityClass) {
            case VAMclassNames.ev_booking:
                let bookingView = new EvBookingView();
                bookingView.setup(args.entityId, args.label, args.additionalParams, args.contextName);
                
                widget = bookingView;
                break;
            case VAMclassNames.ev_projects:
                let projectView = new ProjectView();
                projectView.setup(args.entityId, args.label, args.additionalParams, args.contextName, args.tabContainerName, null, args.targetNode);
                
                widget = projectView;
                break;

            case VAMclassNames.ev_task_single:
            case VAMclassNames.ev_task_type_A:
            case VAMclassNames.vt_ev_task:
                let evtaskView = new EvTaskView();
                evtaskView.setup(args.entityId, args.label, args.contextName, args.tabContainerName, args.additionalParams);
               
                widget = evtaskView;
                break;

            // Adressen
            case AddressClassnames.persons:
            case AddressClassnames.associates:
            case AddressClassnames.contact_persons:
            case AddressClassnames.organisations_external:
            case AddressClassnames.organisations_internal:
            case AddressClassnames.vt_addresses:
            case AddressClassnames.vt_organisations:
                let addressView = new AddressView();

                addressView.setup(args.entityId, args.label, args.additionalParams, args.contextName, args.tabContainerName);
                
                widget = addressView;
                break;

            default:
                let entityView: MdsLax<InstanceType<typeof GenericEntityView>> = new GenericEntityView();
                entityView.setup(args.entityId, args.entityClass, args.label, "ID", args.targetNode ? args.targetNode : null, args.additionalParams, args.contextName, args.tabContainerName);
                widget = entityView;
                break;
        }

        if (widget && args.nonInteractive) widget.setNonInteractive(args.nonInteractive);
        if (args.targetNode) {
            widget.showAtNode(args.entityId, args.label, args.targetNode, false);
        }

        if (widget) topic.publish("/Entity/Opened/" + args.entityId, widget);
    }

    postCreate(): void {
        let self: any = this;
        super.postCreate.apply(this, arguments as any);

        this.subscriptions = [];

        this.subscriptions.push({
            topic: "/Layout/UpdateTopbarMargin",
            handle: topic.subscribe("/Layout/UpdateTopbarMargin", lang.hitch(this, this._onTopbarMarginRequest))
        });
         this.subscriptions.push({
            topic: "/LiveCommand/GlobalProgressBar/ShowModal",
            handle: topic.subscribe("/LiveCommand/GlobalProgressBar/ShowModal", lang.hitch(this, this._showProgressbar, "modal"))
        });

        this.subscriptions.push({
            topic: "/LiveCommand/GlobalProgressBar/Remove",
            handle: topic.subscribe("/LiveCommand/GlobalProgressBar/Remove", lang.hitch(this, this._removeProgressbar))
        });

        this.subscriptions.push({
            topic: "/LiveCommand/GlobalProgressBar/Update",
            handle: topic.subscribe("/LiveCommand/GlobalProgressBar/Update", lang.hitch(this, this._updateProgressbar))
        });

        this.subscriptions.push({
            topic: "/Entity/OpenView",
            handle: topic.subscribe("/Entity/OpenView", lang.hitch(this, this._openEntityView))
        });

        this.subscriptions.push({
            topic: "/GenericDialog/Show",
            handle: topic.subscribe("/GenericDialog/Show", lang.hitch(this, this._openGenericDlg))
        });

    }

    destroy() {

        for (let entry of this.subscriptions) {
            entry.handle.remove();
        }

        let self: any = this;
        super.destroy.apply(this, arguments as any);
    }

    startup(): void {
        // required for dojo inheritance to call the superclass
        let self: any = this;
        super.startup.apply(this, arguments as any);

        this.init();
    }
}

export default mdsWidget(GlobalWidgetsLoader);
